import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import PublicNav from "@/components/PublicNav";
import PublicFooter from "@/components/PublicFooter";
import Section from "@/components/Section";
import CinematicShell from "@/components/cinematic/CinematicShell";
import { PricingHangarStill } from "@/components/cinematic/stills";
import SizzlePlayer from "@/components/SizzlePlayer";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Check } from "lucide-react";

const TIER_TO_KEY = {
  recon: "recon_monthly",
  operator: "operator_monthly",
  arsenal: "arsenal_monthly",
};

export default function PricingPage() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [tiers, setTiers] = useState([]);
  const [busySlug, setBusySlug] = useState("");
  const [err, setErr] = useState("");
  const [alignmentStatus, setAlignmentStatus] = useState(null);
  useEffect(() => {
    api.get("/config/pricing").then((r) => setTiers(r.data)).catch(() => setTiers([]));
    if (user && user.role) {
      api.get("/alignment/status/mine").then((r) => setAlignmentStatus(r.data)).catch(() => {});
    }
  }, [user]);

  const startCheckout = async (slug) => {
    // WHITE-LABEL PARTNER stays application-gated
    if (slug === "white-label-partner") {
      nav("/partners"); return;
    }
    // Every purchase button becomes "Apply to be armed" until the org passes the Standard.
    // Server-side is authoritative — this is the UX shortcut so users don't hit a 403.
    if (!user || user.role === false) {
      nav(`/alignment/apply?tier=${slug}`); return;
    }
    if (user.role !== "client" && user.role !== "admin") {
      setErr("Only client accounts can subscribe. Log in as a client to purchase.");
      return;
    }
    if (!alignmentStatus?.approved) {
      nav(`/alignment/apply?tier=${slug}`); return;
    }
    setErr(""); setBusySlug(slug);
    try {
      const lookup_key = TIER_TO_KEY[slug];
      if (!lookup_key) return;
      const { data } = await api.post("/payments/checkout", { lookup_key, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) {
      setErr(formatApiError(e));
      setBusySlug("");
    }
  };

  const ctaLabelFor = (t) => {
    if (t.slug === "white-label-partner") return t.cta_label;
    if (alignmentStatus?.approved) return "Start an order";
    return "Apply to be armed";
  };

  return (
    <>
      <PublicNav />
      <div className="relative dt6-cinematic-vignette" style={{ minHeight: "480px" }}>
        <CinematicShell
          sceneImport={() => import("@/components/cinematic/scenes/PricingHangar")}
          stillSrc={PricingHangarStill}
          testId="pricing-hero-scene"
          className="dt6-cinematic-grain"
          style={{ position: "absolute", inset: 0, zIndex: 0 }}
        />
        <Section className="pt-16 pb-8 relative dt6-cinematic-copy" testId="pricing-hero">
          <div className="dt6-chip dt6-chip-primary mb-4">/ TIERS</div>
          <h1 className="font-heading text-5xl md:text-6xl font-black">Pick your loadout.</h1>
          <p className="mt-4 text-lg text-[color:var(--dt6-muted)] max-w-3xl">
            Monthly retainers with cycle-based licensing. Upgrade or downgrade any time via the Stripe customer portal. Partners get a wholesale sheet.
          </p>
          {err && <div className="mt-4 text-sm text-[color:var(--dt6-danger)] font-mono tracking-[0.06em]" data-testid="pricing-error">{err}</div>}
        </Section>
      </div>

      <Section className="py-8" testId="pricing-grid">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 items-stretch">
          {tiers.map((t) => {
            const isEnterprise = t.slug === "white-label-partner";
            return (
              <div
                key={t.slug}
                className={`dt6-card p-6 flex flex-col relative transition-transform duration-200 ${
                  t.highlight ? "border-[color:var(--dt6-primary)] md:-translate-y-2" : ""
                }`}
                style={
                  t.highlight
                    ? {
                        boxShadow:
                          "inset 0 0 0 1px var(--dt6-primary), 0 20px 60px -30px rgba(178, 34, 52, 0.4)",
                      }
                    : {}
                }
                data-testid={`pricing-tier-${t.slug}`}
              >
                {t.highlight && (
                  <div
                    className="dt6-chip dt6-chip-primary absolute -top-3 left-6"
                    style={{ background: "var(--dt6-surface)" }}
                  >
                    RECOMMENDED
                  </div>
                )}
                {isEnterprise && (
                  <div
                    className="dt6-chip absolute -top-3 right-6 border-[color:var(--dt6-secondary)] text-[color:var(--dt6-secondary)]"
                    style={{ background: "var(--dt6-surface)" }}
                  >
                    PRINCIPAL-LEVEL
                  </div>
                )}
                <div className="font-mono text-[0.7rem] tracking-[0.18em] text-[color:var(--dt6-muted)]">
                  {t.name}
                </div>
                <div className="mt-3 font-heading text-3xl font-bold">{t.price_display}</div>
                <p className="mt-2 text-sm text-[color:var(--dt6-muted)] min-h-[3rem]">{t.audience}</p>
                <ul className="mt-4 space-y-2 text-sm">
                  {t.features.map((f, i) => (
                    <li key={i} className="flex items-start gap-2" data-testid={`pricing-${t.slug}-feature-${i}`}>
                      <Check size={14} strokeWidth={1.5} className="text-[color:var(--dt6-primary)] mt-1 shrink-0" />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
                <div className="mt-auto pt-6">
                  <button
                    onClick={() => startCheckout(t.slug)}
                    className={`dt6-btn w-full justify-center ${
                      t.highlight ? "dt6-btn-primary" : ""
                    } ${isEnterprise ? "dt6-btn-secondary" : ""}`}
                    disabled={busySlug === t.slug}
                    data-testid={`pricing-${t.slug}-cta`}
                  >
                    {busySlug === t.slug
                      ? "REDIRECTING..."
                      : isEnterprise
                      ? "Summon a Principal"
                      : ctaLabelFor(t)}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </Section>

      <Section className="py-12" testId="pricing-sizzle">
        <div className="grid md:grid-cols-3 gap-8 items-center">
          <div className="md:col-span-1">
            <div className="dt6-chip dt6-chip-primary mb-3">/ SEE IT MOVE</div>
            <h2 className="font-heading text-3xl md:text-4xl font-black leading-tight mb-3">
              Sixty seconds. Full stack.
            </h2>
            <p className="text-sm text-[color:var(--dt6-muted)] leading-relaxed">
              A closed-loop look at the DT6 arsenal — the Source→Clean→Identify→Score→Route→Activate
              flywheel running as it does in production.  Share it with a principal before the call.
            </p>
          </div>
          <div className="md:col-span-2">
            <SizzlePlayer testId="pricing-sizzle-player" />
          </div>
        </div>
      </Section>

      <Section className="py-12" testId="pricing-note">
        <div className="dt6-card p-6 text-sm text-[color:var(--dt6-muted)]">
          <strong className="text-[color:var(--dt6-text)]">Note.</strong> DT6 qualifies every engagement.
          We work with America-First campaigns, PACs, party committees, consultants, and movement organizations.
          Payments run in Stripe <span className="font-mono uppercase text-[color:var(--dt6-secondary)]">TEST-MODE</span> until KYC approval. Card <span className="font-mono">4242 4242 4242 4242</span>, any future expiry, any CVC. WHITE-LABEL PARTNER remains application-gated.
        </div>
      </Section>

      <PublicFooter />
    </>
  );
}
